import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './SlotManagement.css';

function SlotManagement() {
  const [dashboard, setDashboard] = useState(null);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [surgeModal, setSurgeModal] = useState(null);
  const [newSurge, setNewSurge] = useState(1.0);
  const [newCapacity, setNewCapacity] = useState(15);
  const [newBaseIncentive, setNewBaseIncentive] = useState(30);
  const [generateModal, setGenerateModal] = useState(false);
  const [generateDates, setGenerateDates] = useState({ startDate: '', endDate: '' });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDates, setDeleteDates] = useState({ startDate: '', endDate: '', deleteAll: false });
  const [bookingFilters, setBookingFilters] = useState({ date: '', status: '', workAreaId: '' });

  // City penalty settings state
  const [citySettings, setCitySettings] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [editingCity, setEditingCity] = useState(null);
  const [editingGlobal, setEditingGlobal] = useState(false);
  const [cityForm, setCityForm] = useState({ missedSlotPenalty: 20, minCancellationHours: 2, baseSlotIncentive: 30 });
  const [globalForm, setGlobalForm] = useState({ missedSlotPenalty: 20, minCancellationHours: 2, baseSlotIncentive: 30 });

  // Partner slot limits state
  const [partnerLimitsData, setPartnerLimitsData] = useState(null);
  const [partnerLimitsSearch, setPartnerLimitsSearch] = useState('');
  const [editingPartnerLimits, setEditingPartnerLimits] = useState(null);
  const [partnerLimitsForm, setPartnerLimitsForm] = useState({ maxSlotsPerDay: null, maxSlotsPerWeek: null, customLimitReason: '' });

  // Global slot limits state
  const [globalLimits, setGlobalLimits] = useState(null);
  const [globalLimitsLoading, setGlobalLimitsLoading] = useState(false);
  const [globalLimitsError, setGlobalLimitsError] = useState(null);
  const [editingGlobalLimits, setEditingGlobalLimits] = useState(false);
  const [globalLimitsForm, setGlobalLimitsForm] = useState({
    maxSlotsPerDay: 3,
    maxSlotsPerWeek: 14,
    advanceBookingDays: 7,
    minCancellationHours: 2,
    minBookingHoursBeforeSlot: 0,
    baseSlotIncentive: 30,
    peakHourMultiplier: 1.5,
    completionBonusAmount: 20,
    missedSlotPenalty: 20,
    defaultSlotCapacity: 15,
    enabled: true
  });

  // Paid gig booking state
  const [gigSettings, setGigSettings] = useState(null);
  const [gigForm, setGigForm] = useState({
    enabled: false,
    requireGigForOrders: false,
    defaultBookingFee: 50,
    minAdvanceBookingHours: 24,
    minDeliveriesForRefund: 1,
    refundOnCompletion: true,
    cancellationRefundHours: 24
  });
  const [exemptPartners, setExemptPartners] = useState([]);
  const [exemptSearch, setExemptSearch] = useState('');
  const [exemptReason, setExemptReason] = useState('');
  const [gigSaving, setGigSaving] = useState(false);
  const [gigError, setGigError] = useState(null); // FRONT-M5

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, statsRes] = await Promise.all([
        adminAPI.getSlotDashboard(selectedDate),
        adminAPI.getSlotStats({})
      ]);
      setDashboard(dashboardRes.data.data);
      setStats(statsRes.data.data);
    } catch (error) {
      console.error('Failed to fetch slot data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await adminAPI.getSlotBookings(bookingFilters);
      setBookings(response.data.data.bookings);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const fetchCitySettings = async () => {
    try {
      const response = await adminAPI.getCitySlotSettings();
      setCitySettings(response.data.data.citySettings);
      setGlobalSettings(response.data.data.globalSettings);
      setGlobalForm(response.data.data.globalSettings);
    } catch (error) {
      console.error('Failed to fetch city settings:', error);
    }
  };

  const fetchPartnerLimits = async () => {
    try {
      const response = await adminAPI.getPartnersWithCustomLimits();
      setPartnerLimitsData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch partner limits:', error);
    }
  };

  const fetchGlobalLimits = async () => {
    setGlobalLimitsLoading(true);
    setGlobalLimitsError(null);
    try {
      const response = await adminAPI.getGlobalSlotLimits();
      console.log('Global limits response:', response.data);
      if (response.data.success && response.data.data) {
        setGlobalLimits(response.data.data);
        setGlobalLimitsForm(response.data.data);
      } else {
        setGlobalLimitsError('Failed to load global limits');
      }
    } catch (error) {
      console.error('Failed to fetch global limits:', error);
      setGlobalLimitsError(error.response?.data?.message || 'Failed to fetch global limits');
    } finally {
      setGlobalLimitsLoading(false);
    }
  };

  const fetchGigData = async () => {
    setGigError(null);
    try {
      const [settingsRes, exemptRes] = await Promise.all([
        adminAPI.getGigSettings(),
        adminAPI.getGigExemptPartners()
      ]);
      if (settingsRes.data.success) {
        const gs = settingsRes.data.data || {};
        setGigSettings(gs);
        setGigForm({
          enabled: gs.enabled ?? false,
          requireGigForOrders: gs.requireGigForOrders ?? false,
          defaultBookingFee: gs.defaultBookingFee ?? 50,
          minAdvanceBookingHours: gs.minAdvanceBookingHours ?? 24,
          minDeliveriesForRefund: gs.minDeliveriesForRefund ?? 1,
          refundOnCompletion: gs.refundOnCompletion ?? true,
          cancellationRefundHours: gs.cancellationRefundHours ?? 24
        });
      }
      if (exemptRes.data.success) {
        setExemptPartners(exemptRes.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch gig data:', error);
      // FRONT-M5: surface the failure instead of silently showing an empty
      // (and misleading) gig-exemption list / default settings.
      setGigError(error.response?.data?.message || 'Failed to load gig settings. Please retry.');
    }
  };

  useEffect(() => {
    if (activeTab === 'bookings') {
      fetchBookings();
    }
    if (activeTab === 'penalties') {
      fetchCitySettings();
    }
    if (activeTab === 'partnerLimits') {
      fetchPartnerLimits();
    }
    if (activeTab === 'globalLimits') {
      fetchGlobalLimits();
    }
    if (activeTab === 'gigBooking') {
      fetchGigData();
    }
  }, [activeTab, bookingFilters]);

  const handleUpdateCitySettings = async (cityId) => {
    try {
      await adminAPI.updateCitySlotSettings(cityId, cityForm);
      setEditingCity(null);
      fetchCitySettings();
      alert('City penalty settings updated successfully!');
    } catch (error) {
      console.error('Failed to update city settings:', error);
      alert(error.response?.data?.message || 'Failed to update city settings');
    }
  };

  const handleDeleteCitySettings = async (cityId, cityName) => {
    if (!window.confirm(`Remove custom settings for ${cityName}? It will revert to global settings.`)) {
      return;
    }
    try {
      await adminAPI.deleteCitySlotSettings(cityId);
      fetchCitySettings();
      alert('City settings removed. Using global settings now.');
    } catch (error) {
      console.error('Failed to delete city settings:', error);
      alert(error.response?.data?.message || 'Failed to delete city settings');
    }
  };

  const handleUpdateGlobalSettings = async () => {
    try {
      await adminAPI.updateGlobalSlotSettings(globalForm);
      setEditingGlobal(false);
      fetchCitySettings();
      alert('Global penalty settings updated successfully!');
    } catch (error) {
      console.error('Failed to update global settings:', error);
      alert(error.response?.data?.message || 'Failed to update global settings');
    }
  };

  const startEditingCity = (city) => {
    setEditingCity(city.cityId);
    setCityForm({
      missedSlotPenalty: city.missedSlotPenalty,
      minCancellationHours: city.minCancellationHours,
      baseSlotIncentive: city.baseSlotIncentive
    });
  };

  // Partner Slot Limits Functions
  const handleSearchPartner = async () => {
    if (!partnerLimitsSearch.trim()) {
      alert('Please enter a partner phone number or name');
      return;
    }
    try {
      // Search by phone/name in delivery partners
      const response = await adminAPI.getAllDeliveryPartners({ search: partnerLimitsSearch });
      if (response.data.data.partners?.length > 0) {
        const partner = response.data.data.partners[0];
        const limitsRes = await adminAPI.getPartnerSlotLimits(partner._id);
        setEditingPartnerLimits(limitsRes.data.data);
        setPartnerLimitsForm({
          maxSlotsPerDay: limitsRes.data.data.partnerLimits.maxSlotsPerDay,
          maxSlotsPerWeek: limitsRes.data.data.partnerLimits.maxSlotsPerWeek,
          customLimitReason: limitsRes.data.data.partnerLimits.customLimitReason || ''
        });
      } else {
        alert('No delivery partner found with that search term');
      }
    } catch (error) {
      console.error('Failed to search partner:', error);
      alert(error.response?.data?.message || 'Failed to search partner');
    }
  };

  const handleUpdatePartnerLimits = async () => {
    if (!editingPartnerLimits) return;
    try {
      await adminAPI.updatePartnerSlotLimits(editingPartnerLimits.partnerId, partnerLimitsForm);
      alert(`Slot limits updated for ${editingPartnerLimits.partnerName}`);
      setEditingPartnerLimits(null);
      fetchPartnerLimits();
    } catch (error) {
      console.error('Failed to update partner limits:', error);
      alert(error.response?.data?.message || 'Failed to update partner limits');
    }
  };

  const handleResetPartnerLimits = async (partnerId, partnerName) => {
    if (!window.confirm(`Reset slot limits for ${partnerName} to global defaults?`)) {
      return;
    }
    try {
      await adminAPI.resetPartnerSlotLimits(partnerId);
      alert(`Slot limits reset for ${partnerName}`);
      setEditingPartnerLimits(null);
      fetchPartnerLimits();
    } catch (error) {
      console.error('Failed to reset partner limits:', error);
      alert(error.response?.data?.message || 'Failed to reset partner limits');
    }
  };

  // Paid gig booking handlers
  const handleSaveGigSettings = async () => {
    setGigSaving(true);
    try {
      await adminAPI.updateGigSettings(gigForm);
      alert('Gig booking settings saved!');
      fetchGigData();
    } catch (error) {
      console.error('Failed to save gig settings:', error);
      alert(error.response?.data?.message || 'Failed to save gig settings');
    } finally {
      setGigSaving(false);
    }
  };

  const handleGrantExemption = async () => {
    if (!exemptSearch.trim()) {
      alert('Enter a partner phone number or name to search');
      return;
    }
    try {
      const response = await adminAPI.getAllDeliveryPartners({ search: exemptSearch });
      const partner = response.data.data.partners?.[0];
      if (!partner) {
        alert('No delivery partner found with that search term');
        return;
      }
      if (!window.confirm(`Grant gig exemption to ${partner.name} (${partner.phone})? They will receive orders without booking gigs.`)) {
        return;
      }
      await adminAPI.toggleGigExemption(partner._id, {
        exempt: true,
        reason: exemptReason || 'Admin exemption'
      });
      alert(`Gig exemption granted to ${partner.name}`);
      setExemptSearch('');
      setExemptReason('');
      fetchGigData();
    } catch (error) {
      console.error('Failed to grant exemption:', error);
      alert(error.response?.data?.message || 'Failed to grant exemption');
    }
  };

  const handleRevokeExemption = async (partnerId, partnerName) => {
    if (!window.confirm(`Revoke gig exemption for ${partnerName}? They will need to book gigs to receive orders.`)) {
      return;
    }
    try {
      await adminAPI.toggleGigExemption(partnerId, { exempt: false });
      alert(`Gig exemption revoked for ${partnerName}`);
      fetchGigData();
    } catch (error) {
      console.error('Failed to revoke exemption:', error);
      alert(error.response?.data?.message || 'Failed to revoke exemption');
    }
  };

  const handleEditPartnerFromList = async (partnerId) => {
    try {
      const response = await adminAPI.getPartnerSlotLimits(partnerId);
      setEditingPartnerLimits(response.data.data);
      setPartnerLimitsForm({
        maxSlotsPerDay: response.data.data.partnerLimits.maxSlotsPerDay,
        maxSlotsPerWeek: response.data.data.partnerLimits.maxSlotsPerWeek,
        customLimitReason: response.data.data.partnerLimits.customLimitReason || ''
      });
    } catch (error) {
      console.error('Failed to get partner limits:', error);
      alert(error.response?.data?.message || 'Failed to get partner limits');
    }
  };

  const handleUpdateGlobalLimits = async () => {
    try {
      await adminAPI.updateGlobalSlotLimits(globalLimitsForm);
      setEditingGlobalLimits(false);
      fetchGlobalLimits();
      alert('Global slot limits updated successfully!');
    } catch (error) {
      console.error('Failed to update global limits:', error);
      alert(error.response?.data?.message || 'Failed to update global limits');
    }
  };

  const handleUpdateSurge = async () => {
    if (!surgeModal) return;
    // M1: clamp to valid ranges so a NaN / out-of-range keyboard entry can't
    // persist a bad multiplier that skews partner incentive/payout math.
    const safeSurge = Number.isFinite(newSurge) ? Math.min(5, Math.max(1, newSurge)) : 1.0;
    const safeCapacity = Number.isFinite(newCapacity) ? Math.max(0, Math.round(newCapacity)) : 0;
    const safeBase = Number.isFinite(newBaseIncentive) ? Math.max(0, newBaseIncentive) : 0;
    try {
      await adminAPI.updateSlotSurge(surgeModal.templateId, surgeModal.slotId, {
        surgeMultiplier: safeSurge,
        capacity: safeCapacity,
        baseIncentive: safeBase
      });
      setSurgeModal(null);
      fetchData();
      alert(`Slot updated successfully! Base: ₹${newBaseIncentive}, Surge: ${newSurge}x, Capacity: ${newCapacity}`);
    } catch (error) {
      console.error('Failed to update slot:', error);
      alert(error.response?.data?.message || 'Failed to update slot');
    }
  };

  const handleBulkSurge = async (slotId, surge) => {
    try {
      await adminAPI.bulkUpdateSlotSurge({
        date: selectedDate,
        slotId,
        surgeMultiplier: surge
      });
      fetchData();
      alert(`Bulk surge updated for ${slotId} to ${surge}x`);
    } catch (error) {
      console.error('Failed to bulk update surge:', error);
      alert('Failed to bulk update surge');
    }
  };

  const handleRegenerateEmpty = async () => {
    if (!window.confirm('Regenerate empty dates?\n\nFor the upcoming booking window, this rebuilds slots from your CURRENT active gigs on dates that have NO bookings (so gig edits take effect). Dates that already have bookings are left untouched.')) {
      return;
    }
    try {
      const response = await adminAPI.regenerateEmptySlotTemplates();
      alert(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to regenerate templates:', error);
      alert('Failed to regenerate: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleGenerateTemplates = async () => {
    if (!generateDates.startDate) {
      alert('Please select a start date');
      return;
    }
    try {
      const response = await adminAPI.generateSlotTemplates({
        startDate: generateDates.startDate,
        endDate: generateDates.endDate || generateDates.startDate
      });
      setGenerateModal(false);
      alert(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to generate templates:', error);
      alert('Failed to generate templates');
    }
  };

  const handleDeleteTemplates = async () => {
    if (!deleteDates.deleteAll && !deleteDates.startDate) {
      alert('Please select a start date or check "Delete All"');
      return;
    }

    const confirmMsg = deleteDates.deleteAll
      ? 'Are you sure you want to delete ALL slot templates? This cannot be undone!'
      : `Are you sure you want to delete templates from ${deleteDates.startDate}${deleteDates.endDate ? ' to ' + deleteDates.endDate : ''}?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      let response;
      if (deleteDates.deleteAll) {
        response = await adminAPI.bulkDeleteSlotTemplates({ deleteAll: true });
      } else {
        // For date range, we need to delete each date
        const startDate = new Date(deleteDates.startDate);
        const endDate = deleteDates.endDate ? new Date(deleteDates.endDate) : startDate;
        let totalDeleted = 0;
        let totalSkipped = 0;

        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const dateStr = currentDate.toISOString().split('T')[0];
          const res = await adminAPI.bulkDeleteSlotTemplates({ date: dateStr });
          totalDeleted += res.data.data?.deletedCount || 0;
          totalSkipped += res.data.data?.skippedCount || 0;
          currentDate.setDate(currentDate.getDate() + 1);
        }

        response = {
          data: {
            message: `Deleted ${totalDeleted} templates, skipped ${totalSkipped} with bookings`
          }
        };
      }

      setDeleteModal(false);
      setDeleteDates({ startDate: '', endDate: '', deleteAll: false });
      alert(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to delete templates:', error);
      alert('Failed to delete templates: ' + (error.response?.data?.message || error.message));
    }
  };

  const getSlotColor = (slot) => {
    const utilization = slot.bookedCount / slot.capacity;
    if (utilization >= 0.9) return 'slot-full';
    if (utilization >= 0.6) return 'slot-busy';
    if (utilization >= 0.3) return 'slot-moderate';
    return 'slot-available';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'booked': { bg: '#e3f2fd', color: '#1976d2' },
      'checked_in': { bg: '#e8f5e9', color: '#388e3c' },
      'completed': { bg: '#c8e6c9', color: '#2e7d32' },
      'missed': { bg: '#ffebee', color: '#c62828' },
      'cancelled': { bg: '#fafafa', color: '#757575' }
    };
    const style = colors[status] || colors.booked;
    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        textTransform: 'uppercase'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) return <div className="loading">Loading slot management...</div>;

  return (
    <div className="slot-management-page">
      <Navbar />
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1>📅 Slot Booking Management</h1>
          <p>Manage delivery partner time slots and surge pricing</p>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Live Dashboard
          </button>
          <button
            className={`tab ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            📋 All Bookings
          </button>
          <button
            className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📈 Statistics
          </button>
          <button
            className={`tab ${activeTab === 'penalties' ? 'active' : ''}`}
            onClick={() => setActiveTab('penalties')}
          >
            💰 City Penalties
          </button>
          <button
            className={`tab ${activeTab === 'partnerLimits' ? 'active' : ''}`}
            onClick={() => setActiveTab('partnerLimits')}
          >
            👤 Partner Limits
          </button>
          <button
            className={`tab ${activeTab === 'globalLimits' ? 'active' : ''}`}
            onClick={() => setActiveTab('globalLimits')}
          >
            🌐 Global Limits
          </button>
          <button
            className={`tab ${activeTab === 'gigBooking' ? 'active' : ''}`}
            onClick={() => setActiveTab('gigBooking')}
          >
            🎫 Paid Gig Booking
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <>
            {/* Controls */}
            <div className="controls-bar">
              <div className="date-picker">
                <label>Select Date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
              <div className="controls-buttons">
                <button className="btn-primary" onClick={() => setGenerateModal(true)}>
                  ➕ Generate Templates
                </button>
                <button className="btn-secondary" onClick={handleRegenerateEmpty} title="Rebuild upcoming slots from current active gigs on dates with no bookings (applies gig edits; preserves booked dates)">
                  ♻️ Regenerate (empty dates)
                </button>
                <button className="btn-danger" onClick={() => setDeleteModal(true)}>
                  🗑️ Delete Templates
                </button>
              </div>
            </div>

            {/* Summary Stats */}
            {dashboard && (
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="summary-icon">🏢</div>
                  <div className="summary-value">{dashboard.summary.totalWorkAreas}</div>
                  <div className="summary-label">Work Areas</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon">🕐</div>
                  <div className="summary-value">{dashboard.summary.totalSlots}</div>
                  <div className="summary-label">Total Slots</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon">👥</div>
                  <div className="summary-value">{dashboard.summary.totalCapacity}</div>
                  <div className="summary-label">Total Capacity</div>
                </div>
                <div className="summary-card highlight">
                  <div className="summary-icon">✅</div>
                  <div className="summary-value">{dashboard.summary.totalBooked}</div>
                  <div className="summary-label">Booked</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon">📊</div>
                  <div className="summary-value">{dashboard.summary.utilizationRate}%</div>
                  <div className="summary-label">Utilization</div>
                </div>
              </div>
            )}

            {/* Bulk Surge Actions */}
            <div className="bulk-surge-section">
              <h3>⚡ Quick Surge Update (All Work Areas)</h3>
              <div className="bulk-surge-grid">
                {['slot_1', 'slot_2', 'slot_3', 'slot_4', 'slot_5', 'slot_6'].map((slotId, idx) => {
                  const slotNames = ['Morning', 'Lunch', 'Afternoon', 'Evening Peak', 'Night', 'Late Night'];
                  return (
                    <div key={slotId} className="bulk-surge-item">
                      <span className="slot-name">{slotNames[idx]}</span>
                      <div className="surge-buttons">
                        <button onClick={() => handleBulkSurge(slotId, 1.0)}>1.0x</button>
                        <button onClick={() => handleBulkSurge(slotId, 1.25)}>1.25x</button>
                        <button onClick={() => handleBulkSurge(slotId, 1.5)}>1.5x</button>
                        <button onClick={() => handleBulkSurge(slotId, 2.0)}>2.0x</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Work Areas Grid */}
            <div className="work-areas-grid">
              {dashboard?.workAreas.map((workArea) => (
                <div key={workArea.workAreaId} className="work-area-card">
                  <div className="work-area-header">
                    <h3>📍 {workArea.workAreaName}</h3>
                    <span className="city-badge">{workArea.city}</span>
                  </div>

                  {workArea.hasTemplate ? (
                    <div className="slots-grid">
                      {workArea.slots.map((slot) => (
                        <div
                          key={slot.slotId}
                          className={`slot-card ${getSlotColor(slot)}`}
                          onClick={() => {
                            setSurgeModal({
                              ...slot,
                              templateId: workArea.templateId,
                              workAreaName: workArea.workAreaName
                            });
                            setNewSurge(slot.surgeMultiplier || 1.0);
                            setNewCapacity(slot.capacity || 15);
                            setNewBaseIncentive(slot.baseIncentive || 30);
                          }}
                        >
                          <div className="slot-header">
                            <span className="slot-name">{slot.name}</span>
                            {slot.surgeMultiplier > 1 && (
                              <span className="surge-badge">⚡{slot.surgeMultiplier}x</span>
                            )}
                          </div>
                          <div className="slot-time">{slot.startTime} - {slot.endTime}</div>
                          <div className="slot-stats">
                            <div className="booking-count">
                              <strong>{slot.bookedCount}</strong>/{slot.capacity}
                            </div>
                            <div className="incentive">₹{slot.effectiveIncentive}</div>
                          </div>
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${(slot.bookedCount / slot.capacity) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-template">
                      <p>No slots configured for this date</p>
                      <button onClick={() => setGenerateModal(true)}>Generate</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <>
            <div className="filters-bar">
              <input
                type="date"
                value={bookingFilters.date}
                onChange={(e) => setBookingFilters({...bookingFilters, date: e.target.value})}
                placeholder="Filter by date"
              />
              <select
                value={bookingFilters.status}
                onChange={(e) => setBookingFilters({...bookingFilters, status: e.target.value})}
              >
                <option value="">All Statuses</option>
                <option value="booked">Booked</option>
                <option value="checked_in">Checked In</option>
                <option value="completed">Completed</option>
                <option value="missed">Missed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button onClick={fetchBookings}>🔍 Search</button>
            </div>

            <div className="bookings-table-container">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>Work Area</th>
                    <th>Date</th>
                    <th>Slot</th>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Incentive</th>
                    <th>Penalty</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.bookingId}>
                      <td>
                        <div className="partner-info">
                          <strong>{booking.partnerName}</strong>
                          <small>{booking.partnerPhone}</small>
                        </div>
                      </td>
                      <td>{booking.workAreaName}</td>
                      <td>{new Date(booking.date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                      <td>{booking.slotName}</td>
                      <td>{booking.startTime} - {booking.endTime}</td>
                      <td>{getStatusBadge(booking.status)}</td>
                      <td className="amount positive">
                        {booking.incentiveEarned > 0 ? `₹${booking.incentiveEarned}` : '-'}
                      </td>
                      <td className="amount negative">
                        {booking.penaltyApplied > 0 ? `₹${booking.penaltyApplied}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bookings.length === 0 && (
                <div className="no-data">No bookings found</div>
              )}
            </div>
          </>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <>
            <div className="stats-summary">
              <div className="stats-card">
                <h3>📊 Overview ({stats.period.startDate} to {stats.period.endDate})</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-value">{stats.summary.totalBooked}</span>
                    <span className="stat-label">Total Bookings</span>
                  </div>
                  <div className="stat-item success">
                    <span className="stat-value">{stats.summary.totalCompleted}</span>
                    <span className="stat-label">Completed</span>
                  </div>
                  <div className="stat-item warning">
                    <span className="stat-value">{stats.summary.totalMissed}</span>
                    <span className="stat-label">Missed</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{stats.summary.totalCancelled}</span>
                    <span className="stat-label">Cancelled</span>
                  </div>
                  <div className="stat-item highlight">
                    <span className="stat-value">{stats.summary.completionRate}%</span>
                    <span className="stat-label">Completion Rate</span>
                  </div>
                </div>
              </div>

              <div className="stats-card">
                <h3>💰 Financial Summary</h3>
                <div className="financial-stats">
                  <div className="financial-item positive">
                    <span className="label">Total Incentives Paid</span>
                    <span className="value">₹{stats.summary.totalIncentives.toLocaleString()}</span>
                  </div>
                  <div className="financial-item negative">
                    <span className="label">Total Penalties Collected</span>
                    <span className="value">₹{stats.summary.totalPenalties.toLocaleString()}</span>
                  </div>
                  <div className="financial-item net">
                    <span className="label">Net Cost</span>
                    <span className="value">₹{stats.summary.netCost.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="stats-card">
                <h3>📅 Daily Breakdown</h3>
                <div className="daily-stats-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Booked</th>
                        <th>Completed</th>
                        <th>Missed</th>
                        <th>Cancelled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.dailyStats.slice(-7).map((day) => (
                        <tr key={day.date}>
                          <td>{new Date(day.date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                          <td>{day.booked}</td>
                          <td className="success">{day.completed}</td>
                          <td className="warning">{day.missed}</td>
                          <td>{day.cancelled}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* City Penalties Tab */}
        {activeTab === 'penalties' && (
          <>
            <div className="penalties-section">
              {/* Global Settings Card */}
              <div className="settings-card global-settings">
                <div className="settings-header">
                  <h3>🌐 Global Default Settings</h3>
                  <p className="settings-note">Applied to cities without custom settings</p>
                </div>
                {globalSettings && (
                  <div className="settings-content">
                    {editingGlobal ? (
                      <div className="edit-form">
                        <div className="form-row">
                          <label>Penalty Amount (₹):</label>
                          <input
                            type="number"
                            min="0"
                            value={globalForm.missedSlotPenalty}
                            onChange={(e) => setGlobalForm({...globalForm, missedSlotPenalty: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div className="form-row">
                          <label>Min Cancellation Hours:</label>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={globalForm.minCancellationHours}
                            onChange={(e) => setGlobalForm({...globalForm, minCancellationHours: parseInt(e.target.value) || 2})}
                          />
                        </div>
                        <div className="form-row">
                          <label>Base Incentive (₹):</label>
                          <input
                            type="number"
                            min="0"
                            value={globalForm.baseSlotIncentive}
                            onChange={(e) => setGlobalForm({...globalForm, baseSlotIncentive: parseInt(e.target.value) || 30})}
                          />
                        </div>
                        <div className="form-actions">
                          <button className="btn-secondary" onClick={() => setEditingGlobal(false)}>Cancel</button>
                          <button className="btn-primary" onClick={handleUpdateGlobalSettings}>Save</button>
                        </div>
                      </div>
                    ) : (
                      <div className="settings-display">
                        <div className="setting-item">
                          <span className="label">Late Cancel/Miss Penalty:</span>
                          <span className="value">₹{globalSettings.missedSlotPenalty}</span>
                        </div>
                        <div className="setting-item">
                          <span className="label">Min Cancellation Hours:</span>
                          <span className="value">{globalSettings.minCancellationHours} hours</span>
                        </div>
                        <div className="setting-item">
                          <span className="label">Base Incentive:</span>
                          <span className="value">₹{globalSettings.baseSlotIncentive}</span>
                        </div>
                        <button className="btn-edit" onClick={() => setEditingGlobal(true)}>✏️ Edit Global</button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* City-wise Settings */}
              <div className="city-settings-list">
                <h3>🏙️ City-wise Penalty Settings</h3>
                <p className="section-note">Customize penalty amounts for each city. Cities without custom settings use global defaults.</p>

                {citySettings && citySettings.length > 0 ? (
                  <table className="city-settings-table">
                    <thead>
                      <tr>
                        <th>City</th>
                        <th>Penalty (₹)</th>
                        <th>Cancel Hours</th>
                        <th>Incentive (₹)</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {citySettings.map((city) => (
                        <tr key={city.cityId} className={city.hasCustomSettings ? 'custom' : 'global'}>
                          <td className="city-name">{city.cityName}</td>
                          {editingCity === city.cityId ? (
                            <>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  value={cityForm.missedSlotPenalty}
                                  onChange={(e) => setCityForm({...cityForm, missedSlotPenalty: parseInt(e.target.value) || 0})}
                                  className="inline-input"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="1"
                                  max="24"
                                  value={cityForm.minCancellationHours}
                                  onChange={(e) => setCityForm({...cityForm, minCancellationHours: parseInt(e.target.value) || 2})}
                                  className="inline-input"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  value={cityForm.baseSlotIncentive}
                                  onChange={(e) => setCityForm({...cityForm, baseSlotIncentive: parseInt(e.target.value) || 30})}
                                  className="inline-input"
                                />
                              </td>
                              <td>
                                <span className="badge custom">Editing</span>
                              </td>
                              <td className="actions">
                                <button className="btn-sm btn-secondary" onClick={() => setEditingCity(null)}>Cancel</button>
                                <button className="btn-sm btn-primary" onClick={() => handleUpdateCitySettings(city.cityId)}>Save</button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="penalty-value">₹{city.missedSlotPenalty}</td>
                              <td>{city.minCancellationHours}h</td>
                              <td>₹{city.baseSlotIncentive}</td>
                              <td>
                                <span className={`badge ${city.hasCustomSettings ? 'custom' : 'global'}`}>
                                  {city.hasCustomSettings ? '✨ Custom' : '🌐 Global'}
                                </span>
                              </td>
                              <td className="actions">
                                <button className="btn-sm btn-edit" onClick={() => startEditingCity(city)}>✏️</button>
                                {city.hasCustomSettings && (
                                  <button
                                    className="btn-sm btn-danger"
                                    onClick={() => handleDeleteCitySettings(city.cityId, city.cityName)}
                                    title="Reset to global"
                                  >
                                    🔄
                                  </button>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">Loading city settings...</p>
                )}
              </div>

              {/* Policy Info */}
              <div className="policy-info">
                <h4>📋 Penalty Policy</h4>
                <ul>
                  <li><strong>Late Cancellation:</strong> Partner cancels less than {globalSettings?.minCancellationHours || 2} hours before slot start → Penalty Applied</li>
                  <li><strong>No Show:</strong> Partner doesn't check in and doesn't cancel → Penalty Applied</li>
                  <li><strong>Early Cancellation:</strong> Partner cancels {globalSettings?.minCancellationHours || 2}+ hours before slot → No Penalty</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Partner Limits Tab */}
        {activeTab === 'partnerLimits' && (
          <>
            <div className="partner-limits-section">
              {/* Search Partner Card */}
              <div className="settings-card">
                <div className="settings-header">
                  <h3>🔍 Search & Edit Partner Limits</h3>
                  <p className="settings-note">Search by phone number or name to customize slot limits</p>
                </div>
                <div className="search-partner-form">
                  <input
                    type="text"
                    placeholder="Enter phone number or name..."
                    value={partnerLimitsSearch}
                    onChange={(e) => setPartnerLimitsSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchPartner()}
                    style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                  <button className="btn-primary" onClick={handleSearchPartner} style={{ marginLeft: '10px' }}>
                    Search
                  </button>
                </div>

                {/* Edit Partner Form */}
                {editingPartnerLimits && (
                  <div className="edit-partner-limits" style={{ marginTop: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '12px' }}>
                      ✏️ Editing: {editingPartnerLimits.partnerName} ({editingPartnerLimits.partnerPhone})
                    </h4>
                    <div className="global-limits-info" style={{ marginBottom: '16px', padding: '10px', background: '#e0f2fe', borderRadius: '6px' }}>
                      <span>🌐 Global Limits: {editingPartnerLimits.globalLimits.maxSlotsPerDay}/day, {editingPartnerLimits.globalLimits.maxSlotsPerWeek}/week</span>
                    </div>
                    <div className="form-row" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Max Slots Per Day:</label>
                        <input
                          type="number"
                          min="1"
                          max="6"
                          placeholder="Use global"
                          value={partnerLimitsForm.maxSlotsPerDay ?? ''}
                          onChange={(e) => setPartnerLimitsForm({
                            ...partnerLimitsForm,
                            maxSlotsPerDay: e.target.value === '' ? null : parseInt(e.target.value)
                          })}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                        <small style={{ color: '#666' }}>Leave empty to use global ({editingPartnerLimits.globalLimits.maxSlotsPerDay})</small>
                      </div>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Max Slots Per Week:</label>
                        <input
                          type="number"
                          min="1"
                          max="42"
                          placeholder="Use global"
                          value={partnerLimitsForm.maxSlotsPerWeek ?? ''}
                          onChange={(e) => setPartnerLimitsForm({
                            ...partnerLimitsForm,
                            maxSlotsPerWeek: e.target.value === '' ? null : parseInt(e.target.value)
                          })}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                        <small style={{ color: '#666' }}>Leave empty to use global ({editingPartnerLimits.globalLimits.maxSlotsPerWeek})</small>
                      </div>
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Reason for Custom Limit:</label>
                      <input
                        type="text"
                        placeholder="e.g., High performer, New partner training..."
                        value={partnerLimitsForm.customLimitReason}
                        onChange={(e) => setPartnerLimitsForm({ ...partnerLimitsForm, customLimitReason: e.target.value })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div className="form-actions" style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
                      <button className="btn-secondary" onClick={() => setEditingPartnerLimits(null)}>Cancel</button>
                      <button
                        className="btn-danger"
                        onClick={() => handleResetPartnerLimits(editingPartnerLimits.partnerId, editingPartnerLimits.partnerName)}
                      >
                        🔄 Reset to Global
                      </button>
                      <button className="btn-primary" onClick={handleUpdatePartnerLimits}>💾 Save Limits</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Partners with Custom Limits */}
              <div className="settings-card" style={{ marginTop: '20px' }}>
                <div className="settings-header">
                  <h3>👥 Partners with Custom Limits</h3>
                  <p className="settings-note">
                    {partnerLimitsData?.totalPartnersWithCustomLimits || 0} partners have custom slot limits
                  </p>
                </div>

                {partnerLimitsData?.globalLimits && (
                  <div className="global-defaults-banner" style={{
                    padding: '12px',
                    background: '#f0fdf4',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <strong>🌐 Global Defaults:</strong> {partnerLimitsData.globalLimits.maxSlotsPerDay} slots/day, {partnerLimitsData.globalLimits.maxSlotsPerWeek} slots/week
                  </div>
                )}

                {partnerLimitsData?.partners?.length > 0 ? (
                  <table className="city-settings-table">
                    <thead>
                      <tr>
                        <th>Partner</th>
                        <th>City</th>
                        <th>Daily Limit</th>
                        <th>Weekly Limit</th>
                        <th>Reason</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnerLimitsData.partners.map((partner) => (
                        <tr key={partner.partnerId}>
                          <td>
                            <div style={{ fontWeight: '500' }}>{partner.name}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>{partner.phone}</div>
                          </td>
                          <td>{partner.workCity || '-'}</td>
                          <td>
                            <span className={partner.customLimits.maxSlotsPerDay !== null ? 'badge custom' : 'badge global'}>
                              {partner.effectiveLimits.maxSlotsPerDay}/day
                            </span>
                          </td>
                          <td>
                            <span className={partner.customLimits.maxSlotsPerWeek !== null ? 'badge custom' : 'badge global'}>
                              {partner.effectiveLimits.maxSlotsPerWeek}/week
                            </span>
                          </td>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {partner.customLimits.customLimitReason || '-'}
                          </td>
                          <td className="actions">
                            <button className="btn-sm btn-edit" onClick={() => handleEditPartnerFromList(partner.partnerId)}>✏️</button>
                            <button
                              className="btn-sm btn-danger"
                              onClick={() => handleResetPartnerLimits(partner.partnerId, partner.name)}
                              title="Reset to global"
                            >
                              🔄
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    No partners with custom limits yet. Use the search above to set custom limits for specific partners.
                  </p>
                )}
              </div>

              {/* Info Box */}
              <div className="policy-info" style={{ marginTop: '20px' }}>
                <h4>📋 Partner Slot Limits</h4>
                <ul>
                  <li><strong>Custom Limits:</strong> Override global limits for specific delivery partners</li>
                  <li><strong>Use Cases:</strong> High performers can get more slots, new partners can be restricted</li>
                  <li><strong>Empty Values:</strong> Leave empty to use global defaults</li>
                  <li><strong>Range:</strong> Daily: 1-6 slots, Weekly: 1-42 slots</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Global Limits Tab */}
        {activeTab === 'globalLimits' && (
          <>
            <div className="global-limits-section">
              <div className="settings-card">
                <div className="settings-header">
                  <h3>🌐 Global Slot Booking Limits</h3>
                  <p>These settings apply to all delivery partners by default. Individual partners can have custom overrides.</p>
                </div>

                {globalLimitsLoading && (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <p>⏳ Loading global limits...</p>
                  </div>
                )}

                {globalLimitsError && (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626', background: '#fef2f2', borderRadius: '8px', margin: '20px' }}>
                    <p>❌ {globalLimitsError}</p>
                    <button className="btn-primary" onClick={fetchGlobalLimits} style={{ marginTop: '10px' }}>
                      🔄 Retry
                    </button>
                  </div>
                )}

                {!globalLimitsLoading && !globalLimitsError && globalLimits && (
                  <div className="global-settings-form" style={{ padding: '20px' }}>
                    {!editingGlobalLimits ? (
                      <>
                        <div className="settings-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                          <div className="setting-item">
                            <label>📅 Max Slots Per Day</label>
                            <div className="setting-value">{globalLimits.maxSlotsPerDay}</div>
                          </div>
                          <div className="setting-item">
                            <label>📆 Max Slots Per Week</label>
                            <div className="setting-value">{globalLimits.maxSlotsPerWeek}</div>
                          </div>
                          <div className="setting-item">
                            <label>⏰ Advance Booking Days</label>
                            <div className="setting-value">{globalLimits.advanceBookingDays} days</div>
                          </div>
                          <div className="setting-item">
                            <label>🚫 Min Cancellation Hours</label>
                            <div className="setting-value">{globalLimits.minCancellationHours} hrs</div>
                          </div>
                          <div className="setting-item">
                            <label>⏰ Min Booking Hours Before</label>
                            <div className="setting-value">{globalLimits.minBookingHoursBeforeSlot || 0} hrs</div>
                          </div>
                          <div className="setting-item">
                            <label>💰 Base Slot Incentive</label>
                            <div className="setting-value">₹{globalLimits.baseSlotIncentive}</div>
                          </div>
                          <div className="setting-item">
                            <label>📈 Peak Hour Multiplier</label>
                            <div className="setting-value">{globalLimits.peakHourMultiplier}x</div>
                          </div>
                          <div className="setting-item">
                            <label>⚠️ Missed Slot Penalty</label>
                            <div className="setting-value">₹{globalLimits.missedSlotPenalty}</div>
                          </div>
                          <div className="setting-item">
                            <label>👥 Default Slot Capacity</label>
                            <div className="setting-value">{globalLimits.defaultSlotCapacity}</div>
                          </div>
                          <div className="setting-item">
                            <label>✅ Slot Booking Enabled</label>
                            <div className="setting-value">{globalLimits.enabled ? 'Yes' : 'No'}</div>
                          </div>
                        </div>
                        <div style={{ marginTop: '20px', textAlign: 'right' }}>
                          <button className="btn-primary" onClick={() => setEditingGlobalLimits(true)}>
                            ✏️ Edit Global Limits
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="settings-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                          <div className="form-group">
                            <label>📅 Max Slots Per Day (1-6)</label>
                            <input
                              type="number"
                              min="1"
                              max="6"
                              value={globalLimitsForm.maxSlotsPerDay}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, maxSlotsPerDay: parseInt(e.target.value) || 3 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>📆 Max Slots Per Week (1-42)</label>
                            <input
                              type="number"
                              min="1"
                              max="42"
                              value={globalLimitsForm.maxSlotsPerWeek}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, maxSlotsPerWeek: parseInt(e.target.value) || 14 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>⏰ Advance Booking Days (1-14)</label>
                            <input
                              type="number"
                              min="1"
                              max="14"
                              value={globalLimitsForm.advanceBookingDays}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, advanceBookingDays: parseInt(e.target.value) || 7 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>🚫 Min Cancellation Hours (1-24)</label>
                            <input
                              type="number"
                              min="1"
                              max="24"
                              value={globalLimitsForm.minCancellationHours}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, minCancellationHours: parseInt(e.target.value) || 2 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>⏰ Min Booking Hours Before Slot (0-24)</label>
                            <input
                              type="number"
                              min="0"
                              max="24"
                              value={globalLimitsForm.minBookingHoursBeforeSlot}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, minBookingHoursBeforeSlot: parseInt(e.target.value) || 0 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>💰 Base Slot Incentive (₹)</label>
                            <input
                              type="number"
                              min="0"
                              value={globalLimitsForm.baseSlotIncentive}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, baseSlotIncentive: parseFloat(e.target.value) || 30 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>📈 Peak Hour Multiplier (1.0-3.0)</label>
                            <input
                              type="number"
                              min="1.0"
                              max="3.0"
                              step="0.1"
                              value={globalLimitsForm.peakHourMultiplier}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, peakHourMultiplier: parseFloat(e.target.value) || 1.5 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>🎁 Completion Bonus (₹, added to base×surge)</label>
                            <input
                              type="number"
                              min="0"
                              max="1000"
                              value={globalLimitsForm.completionBonusAmount}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, completionBonusAmount: parseFloat(e.target.value) || 0 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>⚠️ Missed Slot Penalty (₹)</label>
                            <input
                              type="number"
                              min="0"
                              value={globalLimitsForm.missedSlotPenalty}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, missedSlotPenalty: parseFloat(e.target.value) || 20 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>👥 Default Slot Capacity (5-100)</label>
                            <input
                              type="number"
                              min="5"
                              max="100"
                              value={globalLimitsForm.defaultSlotCapacity}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, defaultSlotCapacity: parseInt(e.target.value) || 15 })}
                            />
                          </div>
                          <div className="form-group">
                            <label>✅ Slot Booking Enabled</label>
                            <select
                              value={globalLimitsForm.enabled ? 'true' : 'false'}
                              onChange={(e) => setGlobalLimitsForm({ ...globalLimitsForm, enabled: e.target.value === 'true' })}
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ marginTop: '20px', textAlign: 'right', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                          <button className="btn-secondary" onClick={() => {
                            setEditingGlobalLimits(false);
                            setGlobalLimitsForm(globalLimits);
                          }}>
                            Cancel
                          </button>
                          <button className="btn-primary" onClick={handleUpdateGlobalLimits}>
                            💾 Save Changes
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="policy-info" style={{ marginTop: '20px' }}>
                <h4>📋 Global Slot Settings Guide</h4>
                <ul>
                  <li><strong>Max Slots Per Day:</strong> Maximum number of slots a partner can book in a single day (1-6)</li>
                  <li><strong>Max Slots Per Week:</strong> Maximum number of slots a partner can book in a week (1-42)</li>
                  <li><strong>Advance Booking Days:</strong> How many days in advance partners can book slots</li>
                  <li><strong>Min Cancellation Hours:</strong> Minimum hours before slot start to cancel without penalty</li>
                  <li><strong>Min Booking Hours Before Slot:</strong> Partners must book at least this many hours before a slot starts (e.g., 2 = can only book slots starting 2+ hours from now, 0 = no restriction)</li>
                  <li><strong>Base Slot Incentive:</strong> Base amount earned for completing a slot</li>
                  <li><strong>Peak Hour Multiplier:</strong> Incentive multiplier for Evening Peak (18:00-21:00) slots</li>
                  <li><strong>Missed Slot Penalty:</strong> Amount deducted when a partner misses a booked slot</li>
                  <li><strong>Default Slot Capacity:</strong> Default number of partners allowed per slot per work area</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Paid Gig Booking Tab */}
        {activeTab === 'gigBooking' && (
          <div className="settings-section">
            {gigError && (
              <div style={{ background: '#fdf2f2', border: '1px solid #e74c3c', color: '#c0392b', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>⚠️ {gigError}</span>
                <button className="btn-primary" onClick={fetchGigData} style={{ padding: '4px 12px' }}>Retry</button>
              </div>
            )}
            <div className="section-card" style={{
              border: gigForm.enabled ? '2px solid #27ae60' : '2px solid #e74c3c',
              borderRadius: '8px', padding: '20px', marginBottom: '20px',
              background: gigForm.enabled ? '#f0faf0' : '#fdf2f2'
            }}>
              <h3 style={{ marginTop: 0 }}>
                {gigForm.enabled ? '🟢 Paid Gig Booking: ENABLED' : '🔴 Paid Gig Booking: DISABLED'}
              </h3>
              <p style={{ color: '#666', fontSize: '14px' }}>
                When enabled, partners must <strong>pay a booking fee</strong> to book gigs
                (refunded on completion). When disabled, partners can still book gigs for free.
                This does <strong>not</strong> control who receives orders — see the setting below.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                <input
                  type="checkbox"
                  checked={gigForm.enabled}
                  onChange={(e) => setGigForm({ ...gigForm, enabled: e.target.checked })}
                />
                Enable Paid Gig Booking System
              </label>
            </div>

            {/* Independent toggle: gate order assignment to gig-booked / exempt partners */}
            <div className="section-card" style={{
              border: gigForm.requireGigForOrders ? '2px solid #27ae60' : '2px solid #e74c3c',
              borderRadius: '8px', padding: '20px', marginBottom: '20px',
              background: gigForm.requireGigForOrders ? '#f0faf0' : '#fdf2f2'
            }}>
              <h3 style={{ marginTop: 0 }}>
                {gigForm.requireGigForOrders
                  ? '🟢 Require Gig Booking for Orders: ENABLED'
                  : '🔴 Require Gig Booking for Orders: DISABLED'}
              </h3>
              <p style={{ color: '#666', fontSize: '14px' }}>
                When enabled, <strong>only gig-booked or exempt partners receive orders</strong>;
                others won't get deliveries until they book (and check in to) a gig. When disabled,
                all available partners receive orders. Works independently of paid gig booking.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                <input
                  type="checkbox"
                  checked={gigForm.requireGigForOrders}
                  onChange={(e) => setGigForm({ ...gigForm, requireGigForOrders: e.target.checked })}
                />
                Only send orders to gig-booked / exempt partners
              </label>
            </div>

            <div className="section-card" style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ marginTop: 0 }}>⚙️ Gig Settings</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={gigForm.refundOnCompletion}
                    onChange={(e) => setGigForm({ ...gigForm, refundOnCompletion: e.target.checked })}
                  />
                  Refund fee on gig completion
                </label>
                <label>
                  Default Booking Fee (₹)
                  <input
                    type="number" min="0"
                    value={gigForm.defaultBookingFee}
                    onChange={(e) => setGigForm({ ...gigForm, defaultBookingFee: Number(e.target.value) })}
                    style={{ width: '100%', padding: '6px' }}
                  />
                </label>
                <label>
                  Min Advance Booking (hours)
                  <input
                    type="number" min="0" max="168"
                    value={gigForm.minAdvanceBookingHours}
                    onChange={(e) => setGigForm({ ...gigForm, minAdvanceBookingHours: Number(e.target.value) })}
                    style={{ width: '100%', padding: '6px' }}
                  />
                </label>
                <label>
                  Min Deliveries for Refund
                  <input
                    type="number" min="0"
                    value={gigForm.minDeliveriesForRefund}
                    onChange={(e) => setGigForm({ ...gigForm, minDeliveriesForRefund: Number(e.target.value) })}
                    style={{ width: '100%', padding: '6px' }}
                  />
                </label>
                <label>
                  Cancellation Refund Window (hours before slot)
                  <input
                    type="number" min="0" max="168"
                    value={gigForm.cancellationRefundHours}
                    onChange={(e) => setGigForm({ ...gigForm, cancellationRefundHours: Number(e.target.value) })}
                    style={{ width: '100%', padding: '6px' }}
                  />
                </label>
              </div>
              <button
                className="btn-primary"
                onClick={handleSaveGigSettings}
                disabled={gigSaving}
                style={{ marginTop: '16px' }}
              >
                {gigSaving ? 'Saving...' : '💾 Save Gig Settings'}
              </button>
              <p style={{ color: '#888', fontSize: '13px', marginTop: '8px' }}>
                Per-slot fees: set in the slot template (peak slots default to 1.5× this fee, off-peak 0.6×).
              </p>
            </div>

            <div className="section-card" style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ marginTop: 0 }}>🎫 Gig-Exempt Partners</h3>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Exempt partners receive orders without booking gigs (treated as gig-booked).
              </p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Partner phone or name"
                  value={exemptSearch}
                  onChange={(e) => setExemptSearch(e.target.value)}
                  style={{ flex: '1', minWidth: '180px', padding: '8px' }}
                />
                <input
                  type="text"
                  placeholder="Reason (optional)"
                  value={exemptReason}
                  onChange={(e) => setExemptReason(e.target.value)}
                  style={{ flex: '1', minWidth: '180px', padding: '8px' }}
                />
                <button className="btn-primary" onClick={handleGrantExemption}>
                  ➕ Grant Exemption
                </button>
              </div>
              {exemptPartners.length === 0 ? (
                <p style={{ color: '#888' }}>No exempt partners.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                      <th style={{ padding: '8px' }}>Name</th>
                      <th style={{ padding: '8px' }}>Phone</th>
                      <th style={{ padding: '8px' }}>Reason</th>
                      <th style={{ padding: '8px' }}>Since</th>
                      <th style={{ padding: '8px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exemptPartners.map((p) => (
                      <tr key={p._id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{p.name}</td>
                        <td style={{ padding: '8px' }}>{p.phone}</td>
                        <td style={{ padding: '8px' }}>{p.deliveryPartner?.gigExemptReason || '—'}</td>
                        <td style={{ padding: '8px' }}>
                          {p.deliveryPartner?.gigExemptSetAt
                            ? new Date(p.deliveryPartner.gigExemptSetAt).toLocaleDateString()
                            : '—'}
                        </td>
                        <td style={{ padding: '8px' }}>
                          <button
                            className="btn-danger"
                            onClick={() => handleRevokeExemption(p._id, p.name)}
                            style={{ padding: '4px 12px', fontSize: '13px' }}
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Slot Settings Modal */}
        {surgeModal && (
          <div className="modal-overlay" onClick={() => setSurgeModal(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>⚙️ Update Slot Settings</h3>
              <div className="modal-content">
                <p><strong>Work Area:</strong> {surgeModal.workAreaName}</p>
                <p><strong>Slot:</strong> {surgeModal.name} ({surgeModal.startTime} - {surgeModal.endTime})</p>
                <p><strong>Currently Booked:</strong> {surgeModal.bookedCount} partners</p>

                <div className="base-incentive-input" style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <label>Base Incentive (₹):</label>
                  <input
                    type="number"
                    min="0"
                    max="500"
                    step="5"
                    value={newBaseIncentive}
                    onChange={(e) => setNewBaseIncentive(parseInt(e.target.value) || 0)}
                    style={{ width: '80px', marginLeft: '10px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <div className="quick-incentive-buttons" style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {[5, 10, 15, 20, 30, 50, 100].map(val => (
                      <button
                        key={val}
                        className={newBaseIncentive === val ? 'active' : ''}
                        onClick={() => setNewBaseIncentive(val)}
                        style={{
                          padding: '6px 12px',
                          border: newBaseIncentive === val ? '2px solid #4caf50' : '1px solid #ddd',
                          borderRadius: '4px',
                          background: newBaseIncentive === val ? '#e8f5e9' : '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        ₹{val}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="capacity-input" style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <label>Slot Capacity (max partners):</label>
                  <input
                    type="number"
                    min={surgeModal.bookedCount || 1}
                    max="100"
                    step="1"
                    value={newCapacity}
                    onChange={(e) => setNewCapacity(parseInt(e.target.value) || 15)}
                    style={{ width: '80px', marginLeft: '10px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <div className="quick-capacity-buttons" style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {[10, 15, 20, 25, 30, 40, 50].map(val => (
                      <button
                        key={val}
                        className={newCapacity === val ? 'active' : ''}
                        onClick={() => setNewCapacity(val)}
                        disabled={val < surgeModal.bookedCount}
                        style={{
                          padding: '6px 12px',
                          border: newCapacity === val ? '2px solid #1976d2' : '1px solid #ddd',
                          borderRadius: '4px',
                          background: newCapacity === val ? '#e3f2fd' : '#fff',
                          cursor: val < surgeModal.bookedCount ? 'not-allowed' : 'pointer',
                          opacity: val < surgeModal.bookedCount ? 0.5 : 1
                        }}
                      >
                        {val}
                      </button>
                    ))}
                  </div>
                  {newCapacity < surgeModal.bookedCount && (
                    <p style={{ color: '#c62828', fontSize: '12px', marginTop: '8px' }}>
                      Cannot reduce below current bookings ({surgeModal.bookedCount})
                    </p>
                  )}
                </div>

                <div className="surge-input" style={{ marginTop: '16px' }}>
                  <label>Surge Multiplier:</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    step="0.25"
                    value={newSurge}
                    onChange={(e) => setNewSurge(parseFloat(e.target.value) || 1.0)}
                  />
                  <span className="surge-preview">
                    = ₹{(newBaseIncentive * newSurge).toFixed(0)} effective
                  </span>
                </div>
                <div className="quick-surge-buttons">
                  {[1.0, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0].map(val => (
                    <button
                      key={val}
                      className={newSurge === val ? 'active' : ''}
                      onClick={() => setNewSurge(val)}
                    >
                      {val}x
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setSurgeModal(null)}>Cancel</button>
                <button className="btn-primary" onClick={handleUpdateSurge}>Update Slot</button>
              </div>
            </div>
          </div>
        )}

        {/* Generate Templates Modal */}
        {generateModal && (
          <div className="modal-overlay" onClick={() => setGenerateModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>➕ Generate Slot Templates</h3>
              <div className="modal-content">
                <p>Generate slot templates for all work areas for the selected date range.</p>
                <div className="form-group">
                  <label>Start Date:</label>
                  <input
                    type="date"
                    value={generateDates.startDate}
                    onChange={(e) => setGenerateDates({...generateDates, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>End Date (optional):</label>
                  <input
                    type="date"
                    value={generateDates.endDate}
                    onChange={(e) => setGenerateDates({...generateDates, endDate: e.target.value})}
                  />
                </div>
                <p className="note">Note: Maximum 14 days can be generated at once.</p>
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setGenerateModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleGenerateTemplates}>Generate</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Templates Modal */}
        {deleteModal && (
          <div className="modal-overlay" onClick={() => setDeleteModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>🗑️ Delete Slot Templates</h3>
              <div className="modal-content">
                <p className="warning-text">⚠️ Templates with existing bookings will be skipped.</p>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={deleteDates.deleteAll}
                      onChange={(e) => setDeleteDates({...deleteDates, deleteAll: e.target.checked})}
                    />
                    Delete ALL templates (dangerous!)
                  </label>
                </div>
                {!deleteDates.deleteAll && (
                  <>
                    <div className="form-group">
                      <label>Start Date:</label>
                      <input
                        type="date"
                        value={deleteDates.startDate}
                        onChange={(e) => setDeleteDates({...deleteDates, startDate: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date (optional):</label>
                      <input
                        type="date"
                        value={deleteDates.endDate}
                        onChange={(e) => setDeleteDates({...deleteDates, endDate: e.target.value})}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setDeleteModal(false)}>Cancel</button>
                <button className="btn-danger" onClick={handleDeleteTemplates}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SlotManagement;
